import { create } from "zustand"
import { track } from "@/lib/analytics"
import { toast } from "@/hooks/use-toast"
import { isDismissed, paywallKey } from "@/lib/paywallSession"

export type QuotaMetric =
  | "nira.message" | "oio_sim.draft" | "oia_sim.draft"
  | "nira.officer_reading" | "nira.notice_reading" | "nira.appeal_reading"
  | "nira.fact_rerun" | "nira.rerun_documents" | "nira.department_reading"
  | "order.draft" | "order.draft_edit"

export type PlanState = "guest" | "exempt" | "free_month" | "free" | "plus" | "pro" | "grace"
/** What the paywall offers: both paid plans, Pro, Pro with this month's Plus credited, an add-on, or only the reset time. */
export type Offer = "plus_or_pro" | "pro" | "pro_credit" | "addon_draft" | "addon_edits" | "none"

const PLAN_STATES: readonly string[] = ["guest", "exempt", "free_month", "free", "plus", "pro", "grace"]
const OFFERS: readonly string[] = ["plus_or_pro", "pro", "pro_credit", "addon_draft", "addon_edits", "none"]

/** The add-on a 402 names (addon_draft / addon_edits offers). */
export interface AddonOffer { sku: string; priceMinor: number | null; quantity: number | null }

/** A 402 from the gateway: the daily/lifetime allowance is used up, or a draft is already open. */
export interface QuotaDenial {
  code: "quota_exhausted" | "draft_in_progress"
  metric: string
  window: string | null
  limit: number | null
  used: number | null
  resetsAt: string | null
  subscribed: boolean
  canSubscribe: boolean
  /** The open draft's session id (draft_in_progress only). */
  sessionId: string | null
  /** The feature the allowance belongs to, as the server tags it (research, notice_analysis, ...). Null when unknown. */
  feature: string | null
  /** The server's user-facing sentence for this denial ("You've used your free notice analysis."). */
  message: string | null
  /** Whether the server lets the reader preview the result without subscribing. Null when not sent. */
  previewAllowed: boolean | null
  /** Opened by an Upgrade button, not by running out: the paywall offers Pro without "you have used". */
  upgrade?: boolean
  /** The reader's plan state as the server saw it. Null from a server that does not say. */
  planState: PlanState | null
  /** Which paywall to draw. Null from a server that does not say; the modal then derives one. */
  offer: Offer | null
  /** The paywall trigger number (1..17) the server fired, if any. */
  trigger: number | null
  /** The add-on to sell (addon offers). */
  addon: AddonOffer | null
  /** Draft session id the denial is scoped to (order.draft_edit). */
  scopeKey: string | null
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null)
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null)

/** Parse a 402 body (top level, or nested under FastAPI's `detail`). Null when it is not a quota body. */
export function parseQuotaDenial(body: unknown): QuotaDenial | null {
  if (!body || typeof body !== "object") return null
  let b = body as Record<string, unknown>
  if (b.code === undefined && b.detail && typeof b.detail === "object") b = b.detail as Record<string, unknown>
  if (b.code !== "quota_exhausted" && b.code !== "draft_in_progress") return null
  return {
    code: b.code,
    metric: str(b.metric) ?? "",
    window: str(b.window),
    limit: num(b.limit),
    used: num(b.used),
    resetsAt: str(b.resets_at),
    subscribed: b.subscribed === true,
    canSubscribe: b.can_subscribe !== false,
    sessionId: str(b.session_id),
    feature: str(b.feature),
    message: str(b.message),
    previewAllowed: typeof b.preview_allowed === "boolean" ? b.preview_allowed : null,
    planState: typeof b.plan_state === "string" && PLAN_STATES.includes(b.plan_state) ? (b.plan_state as PlanState) : null,
    offer: typeof b.offer === "string" && OFFERS.includes(b.offer) ? (b.offer as Offer) : null,
    trigger: num(b.trigger),
    addon: parseAddon(b.addon),
    scopeKey: str(b.scope_key),
  }
}

function parseAddon(v: unknown): AddonOffer | null {
  if (!v || typeof v !== "object") return null
  const a = v as Record<string, unknown>
  const sku = str(a.sku)
  return sku ? { sku, priceMinor: num(a.price_minor), quantity: num(a.quantity) } : null
}

/** A 413 (too large) or 415 (unsupported type) from an upload: the server's own sentence, or null. */
export interface UploadRefusal { status: 413 | 415; code: string; message: string; limitBytes: number | null; feature: string | null }

export function parseUploadRefusal(status: number, body: unknown): UploadRefusal | null {
  if (status !== 413 && status !== 415) return null
  if (!body || typeof body !== "object") return null
  const b = body as Record<string, unknown>
  const message = str(b.message)
  if (!message) return null
  return {
    status,
    code: str(b.code) ?? (status === 413 ? "file_too_large" : "unsupported_type"),
    message,
    limitBytes: num(b.limit_bytes),
    feature: str(b.feature),
  }
}

/** The server's own sentence for a 413 or 415 upload refusal (set by the api client), else null. */
export function uploadRefusalMessage(err: unknown): string | null {
  const e = err as { status?: unknown; code?: unknown; message?: unknown } | null
  if (!e || (e.status !== 413 && e.status !== 415)) return null
  return typeof e.code === "string" && typeof e.message === "string" && e.message ? e.message : null
}

/** A 503 plan_check_unavailable: the plan could not be checked just now. Not a paywall. */
export function isPlanCheckUnavailable(status: number, body: unknown): boolean {
  if (status !== 503 || !body || typeof body !== "object") return false
  return (body as { code?: unknown }).code === "plan_check_unavailable"
}

/** A calm, dismissable notice to retry; it never opens the paywall. */
export function notifyPlanCheckUnavailable(message?: string | null): void {
  toast({
    title: "Couldn't check your plan",
    description: message || "Nira could not check your plan just now. Try again in a moment.",
  })
}

interface QuotaGateState {
  denial: QuotaDenial | null
  show: (d: QuotaDenial) => void
  close: () => void
}

export const useQuotaGate = create<QuotaGateState>((set) => ({
  denial: null,
  show: (denial) => {
    // A real allowance used up (not an Upgrade button): its own event, apart from paywall_shown.
    if (denial.code === "quota_exhausted" && !denial.upgrade) track("quota_reached", { metric: denial.metric, feature: denial.feature })
    set({ denial })
  },
  close: () => set({ denial: null }),
}))

/** The shown line for a paywall the reader chose "Not now" on. Call sites render it inline instead. */
export const PAUSED_LINE = "Paused \u2014 you chose Not now. Upgrade from the account page."

/** True when "Not now" already hid this paywall in this session. An Upgrade button or an open draft is never hidden. */
export const isPaywallDismissed = (d: QuotaDenial): boolean =>
  d.code === "quota_exhausted" && !d.upgrade && isDismissed(paywallKey(d))

/** How many draft pages are mounted that say "edits used" inline (order-sim/editLimit.tsx). */
export const inlineEdits = { mounted: 0 }

/** Open the modal, unless it was dismissed this session (`force`: the reader pressed a button for it). Returns whether it opened. */
export function showQuotaGate(d: QuotaDenial, force = false): boolean {
  if (!force && isPaywallDismissed(d)) return false
  // While a draft page shows its own inline line (trigger 14), the 11th edit's 402 is not also a modal.
  if (!force && inlineEdits.mounted > 0 && d.offer === "addon_edits" && !d.upgrade) return false
  useQuotaGate.getState().show(d)
  return true
}

/**
 * Open the matching modal for a 402 response (any route, stream or JSON), or the calm retry notice for
 * a 503 plan check. Never throws; the body stays readable.
 */
export async function gateFromResponse(res: Response): Promise<QuotaDenial | null> {
  if (res.status !== 402 && res.status !== 503) return null
  try {
    const body = await res.clone().json()
    if (isPlanCheckUnavailable(res.status, body)) {
      notifyPlanCheckUnavailable(str((body as Record<string, unknown>).message))
      return null
    }
    if (res.status !== 402) return null
    const denial = parseQuotaDenial(body)
    if (denial) showQuotaGate(denial)
    return denial
  } catch {
    return null
  }
}

/** Open the modal for an already-parsed 402 body. */
export function gateFromBody(body: unknown): QuotaDenial | null {
  const denial = parseQuotaDenial(body)
  if (denial) showQuotaGate(denial)
  return denial
}

/** "HH:MM IST" for an ISO instant (24-hour clock, Asia/Kolkata). */
export function formatResetIST(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00"
  return `${get("hour")}:${get("minute")} IST`
}

/** Where the open draft lives. */
export function sessionPath(metric: string, sessionId: string): string {
  return `${metric.startsWith("oia_sim") ? "/oia-sim" : "/oio-sim"}/${sessionId}`
}

export interface MetricSummary {
  metric: string
  limit: number | null
  used: number
  reserved?: number
  window: string
  resets_at: string | null
}

/** True when the instant is already behind us. */
export function isPast(iso: string | null, now: number = Date.now()): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return !Number.isNaN(t) && t <= now
}

/** Earliest future-or-past reset among exhausted-or-stale metrics, as epoch ms (null if none). */
export function earliestReset(entitlements: { metrics: MetricSummary[] } | null | undefined): number | null {
  let best: number | null = null
  for (const m of entitlements?.metrics ?? []) {
    if (m.limit === null || !m.resets_at) continue
    if (m.used + (m.reserved ?? 0) < m.limit && !isPast(m.resets_at)) continue
    const t = new Date(m.resets_at).getTime()
    if (!Number.isNaN(t) && (best === null || t < best)) best = t
  }
  return best
}

/** True when `me.entitlements` shows the metric already used up (send buttons pre-disable on this). */
export function metricExhausted(
  entitlements: { exempt?: boolean; metrics: MetricSummary[] } | null | undefined,
  metric: QuotaMetric,
): boolean {
  if (!entitlements || entitlements.exempt) return false
  const m = entitlements.metrics.find((x) => x.metric === metric)
  if (!m || m.limit === null) return false
  if (isPast(m.resets_at)) return false // the window rolled over; /me is stale, a refetch decides
  // Draft metrics: the server supersedes a stale reservation on a new run, so only `used` counts.
  const reserved = metric.endsWith("_sim.draft") || metric === "order.draft" ? 0 : (m.reserved ?? 0)
  return m.used + reserved >= m.limit
}

/** What the doc's OPA table says to offer for a plan state and metric (the same rules, for when no server denial exists). */
export function synthesizeOffer(
  state: PlanState,
  metric: string,
  window: string | null,
  limit: number | null,
): { offer: Offer; trigger: number | null; message: string | null } {
  const paid = state === "plus" || state === "pro" || state === "grace"
  const freeTier = state === "free" || state === "free_month" || state === "plus"
  const lim = limit !== null ? String(limit) : null
  if (state === "pro" || state === "grace") {
    if (metric === "order.draft") return { offer: "addon_draft", trigger: 13, message: "This month's draft is used." }
    if (metric === "order.draft_edit") return { offer: "addon_edits", trigger: 14, message: null }
    if (window === "day") return { offer: "none", trigger: 15, message: "Resets at midnight." }
  }
  switch (metric) {
    case "nira.message":
      if (state === "free_month") return { offer: "none", trigger: 2, message: lim ? `That's ${lim} today; more tomorrow.` : "More tomorrow." }
      if (state === "plus") return { offer: "pro_credit", trigger: 4, message: lim ? `You've used this month's ${lim} questions.` : null }
      if (state === "free") return { offer: "plus_or_pro", trigger: 3, message: lim ? `You've used this month's ${lim} questions.` : null }
      break
    case "nira.notice_reading":
      if (freeTier) return { offer: "pro", trigger: 5, message: "Your free notice analysis is used." }
      break
    case "nira.fact_rerun":
      if (freeTier) return { offer: "pro", trigger: 6, message: "Two re-runs come with the free notice." }
      break
    case "nira.rerun_documents":
      if (freeTier) return { offer: "pro", trigger: 7, message: null }
      break
    case "nira.appeal_reading":
      if (freeTier) return { offer: "pro", trigger: 8, message: "Your free Appeal or not reading is used." }
      break
    case "nira.department_reading":
      if (freeTier) return { offer: "pro", trigger: 10, message: null }
      break
    case "order.draft":
      if (freeTier) return { offer: "pro", trigger: 12, message: "Order drafts come with Pro." }
      break
  }
  if (state === "guest") return { offer: "plus_or_pro", trigger: null, message: null }
  return { offer: paid && state !== "plus" ? "none" : "pro", trigger: null, message: null }
}

/** The plan state `me.entitlements` reports, or one worked out from the older fields. */
export function planStateOf(e: { subscribed: boolean; exempt?: boolean; plan_state?: PlanState | null } | null | undefined): PlanState {
  if (e?.plan_state) return e.plan_state
  if (e?.exempt) return "exempt"
  return e?.subscribed ? "pro" : "free"
}

/**
 * Open the paywall from `me.entitlements` (no 402 needed), e.g. an "Upgrade" button or a call site that
 * knows the allowance is used. The offer is worked out from the plan state with the server's own rules,
 * unless `opts.trigger` names the trigger. `opts.force` (a button the reader pressed) skips the dismissal check. Returns false (and opens nothing) when the reader chose
 * "Not now" on this paywall earlier in the session; the call site shows PAUSED_LINE instead.
 */
export function openPaywall(
  entitlements: { subscribed: boolean; exempt?: boolean; plan_state?: PlanState | null; metrics: MetricSummary[] } | null | undefined,
  metric: QuotaMetric,
  opts: { upgrade?: boolean; trigger?: number; scopeKey?: string; force?: boolean } = {},
): boolean {
  const m = entitlements?.metrics.find((x) => x.metric === metric)
  const subscribed = entitlements?.subscribed ?? false
  const planState = planStateOf(entitlements)
  let syn = synthesizeOffer(planState, metric, m?.window ?? null, m?.limit ?? null)
  // An Upgrade button is a plain offer to move up, whatever the allowance says.
  if (opts.upgrade) {
    const offer: Offer = planState === "plus" ? "pro_credit"
      : planState === "pro" || planState === "grace" || planState === "exempt" ? "none"
        : metric === "nira.message" ? "plus_or_pro" : "pro"
    syn = { ...syn, offer }
  }
  return showQuotaGate({
    code: "quota_exhausted",
    metric,
    window: m?.window ?? null,
    limit: m?.limit ?? null,
    used: m?.used ?? null,
    resetsAt: m?.resets_at ?? null,
    subscribed,
    canSubscribe: planState !== "pro" && planState !== "grace" && planState !== "exempt",
    sessionId: null,
    feature: null,
    message: opts.upgrade ? null : syn.message,
    previewAllowed: null,
    upgrade: opts.upgrade ?? false,
    planState,
    offer: syn.offer,
    trigger: opts.trigger ?? syn.trigger,
    addon: null,
    scopeKey: opts.scopeKey ?? null,
  }, opts.force ?? false)
}
